import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import Image from '../AppImage';

const ShoppingCart = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [cartItems, setCartItems] = useState([
    {
      id: 1,
      name: 'Professional Carpet Cleaning',
      price: 149.99,
      quantity: 1,
      image: '/assets/images/carpet-cleaning.jpg',
      category: 'Service'
    },
    {
      id: 2,
      name: 'Eco-Friendly Floor Cleaner',
      price: 24.99,
      quantity: 2,
      image: '/assets/images/floor-cleaner.jpg',
      category: 'Product'
    }
  ]);

  const toggleCart = () => {
    setIsOpen(!isOpen);
  };

  const closeCart = () => {
    setIsOpen(false);
  };

  const updateQuantity = (id, newQuantity) => {
    if (newQuantity === 0) {
      removeItem(id);
      return;
    }
    setCartItems(items =>
      items?.map(item =>
        item?.id === id ? { ...item, quantity: newQuantity } : item
      )
    );
  };

  const removeItem = (id) => {
    setCartItems(items => items?.filter(item => item?.id !== id));
  };

  const getTotalItems = () => {
    return cartItems?.reduce((total, item) => total + item?.quantity, 0);
  };

  const getTotalPrice = () => {
    return cartItems?.reduce((total, item) => total + (item?.price * item?.quantity), 0);
  };

  const handleCheckout = () => {
    console.log('Proceeding to checkout with items:', cartItems);
    closeCart();
  };

  // Close cart when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && !event?.target?.closest('.cart-container')) {
        closeCart();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Prevent body scroll when cart is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  return (
    <>
      {/* Cart Toggle Button */}
      <div className="fixed top-20 right-4 z-40 lg:top-24 lg:right-6">
        <Button
          variant="default"
          size="icon"
          onClick={toggleCart}
          className="relative shadow-elevation-3 hover:shadow-elevation-4 transition-all duration-200"
          aria-label="Shopping cart"
        >
          <Icon name="ShoppingCart" size={20} />
          {getTotalItems() > 0 && (
            <span className="absolute -top-2 -right-2 bg-error text-white text-xs font-medium rounded-full w-5 h-5 flex items-center justify-center">
              {getTotalItems()}
            </span>
          )}
        </Button>
      </div>
      {/* Cart Overlay */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 lg:bg-transparent lg:pointer-events-none">
          {/* Cart Drawer */}
          <div 
            className={`cart-container fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-elevation-5 transform transition-transform duration-300 lg:pointer-events-auto ${
              isOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
          >
            {/* Cart Header */}
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h2 className="text-lg font-semibold text-text-primary">
                Shopping Cart ({getTotalItems()})
              </h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={closeCart}
                aria-label="Close cart"
              >
                <Icon name="X" size={20} />
              </Button>
            </div>

            {/* Cart Content */}
            <div className="flex flex-col h-full">
              {cartItems?.length === 0 ? (
                /* Empty Cart */
                (<div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
                  <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
                    <Icon name="ShoppingCart" size={32} color="var(--color-text-secondary)" />
                  </div>
                  <h3 className="text-lg font-medium text-text-primary mb-2">
                    Your cart is empty
                  </h3>
                  <p className="text-text-secondary mb-6">
                    Add some services or products to get started
                  </p>
                  <Button
                    variant="default"
                    onClick={closeCart}
                    iconName="ArrowLeft"
                    iconPosition="left"
                  >
                    Continue Shopping
                  </Button>
                </div>)
              ) : (
                <>
                  {/* Cart Items */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {cartItems?.map((item) => (
                      <div key={item?.id} className="flex items-start space-x-3 p-3 bg-surface rounded-lg border border-border">
                        <div className="w-16 h-16 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                          <Image
                            src={item?.image}
                            alt={item?.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h4 className="text-sm font-medium text-text-primary truncate">
                                {item?.name}
                              </h4>
                              <span className="text-xs text-text-secondary">
                                {item?.category}
                              </span>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => removeItem(item?.id)}
                              className="text-text-secondary hover:text-error w-6 h-6"
                              aria-label="Remove item"
                            >
                              <Icon name="Trash2" size={14} />
                            </Button>
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <Button
                                variant="outline"
                                size="icon"
                                onClick={() => updateQuantity(item?.id, item?.quantity - 1)}
                                className="w-6 h-6"
                                disabled={item?.quantity <= 1}
                              >
                                <Icon name="Minus" size={12} />
                              </Button>
                              <span className="text-sm font-medium text-text-primary w-8 text-center">
                                {item?.quantity}
                              </span>
                              <Button
                                variant="outline"
                                size="icon"
                                onClick={() => updateQuantity(item?.id, item?.quantity + 1)}
                                className="w-6 h-6"
                              >
                                <Icon name="Plus" size={12} />
                              </Button>
                            </div>
                            <span className="text-sm font-semibold text-text-primary font-mono">
                              ${(item?.price * item?.quantity)?.toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Cart Footer */}
                  <div className="border-t border-border p-4 space-y-4">
                    {/* Bulk Pricing Notice */}
                    {getTotalPrice() > 200 && (
                      <div className="bg-success/10 border border-success/20 rounded-lg p-3">
                        <div className="flex items-center space-x-2">
                          <Icon name="Tag" size={16} color="var(--color-success)" />
                          <span className="text-sm font-medium text-success">
                            Bulk discount applied!
                          </span>
                        </div>
                        <p className="text-xs text-success/80 mt-1">
                          Save 10% on orders over $200
                        </p>
                      </div>
                    )}

                    {/* Total */}
                    <div className="flex items-center justify-between py-2">
                      <span className="text-base font-medium text-text-primary">
                        Total
                      </span>
                      <span className="text-lg font-bold text-text-primary font-mono">
                        ${getTotalPrice()?.toFixed(2)}
                      </span>
                    </div>

                    {/* Checkout Button */}
                    <Button
                      variant="default"
                      fullWidth
                      onClick={handleCheckout}
                      iconName="CreditCard"
                      iconPosition="left"
                      className="font-medium"
                    >
                      Proceed to Checkout
                    </Button>

                    {/* Continue Shopping */}
                    <Button
                      variant="outline"
                      fullWidth
                      onClick={closeCart}
                      iconName="ArrowLeft"
                      iconPosition="left"
                    >
                      Continue Shopping
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ShoppingCart;